/**
 * ClassCalc — "what class am I in, and what do I need to be legal in it?"
 *
 * A public page. It renders outside `AuthProvider`, `RequireAuth` and
 * `GarageProvider` (see the route table in App.tsx), which is not a convenience:
 * a reference tool that made you sign in to read a rulebook summary would be a
 * worse tool, and every provider it sits outside is one that cannot fire a
 * request on its behalf. Nothing on this page reaches the network, and nothing
 * it touches leaves the browser — §3's non-goals hold because there is no code
 * here that could break them.
 *
 * The screen is one column and three stops: the spec, the classes, the
 * checklist. Results appear underneath the form rather than on a second screen,
 * so changing a number and watching the answer move is one gesture — which is
 * how the thing actually gets used, on a phone, next to the car.
 *
 * One thing here lives in the URL rather than in state: the catalogue. See
 * `classcalc/catalogue.ts` for why that one and nothing else.
 */

import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CircleCheck,
  CircleHelp,
  ExternalLink,
  Flag,
  Gauge,
  IdCard,
  ListChecks,
  Route,
  ShieldCheck,
  Sparkles,
  Stamp,
  TriangleAlert,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

import { Footer } from '../components/Footer';
import { Field, Opts, TrigToggle } from '../components/form';
import { Eyebrow } from '../components/primitives';
import { usePageMeta } from '../lib/meta';
import { QUALITY_TEXT, matchClasses, shortlist } from '../classcalc/classes';
import type { ClassMatch, Org } from '../classcalc/classes';
import { CATALOGUE_PAGE, CLASSCALC_PATH, interestFromSlug, pathForInterest } from '../classcalc/catalogue';
import { DISCLAIMER, RULES_CHECKED, RULES_SEASON, RULE_SOURCES } from '../classcalc/sources';
import { checklistByCategory, tierCounts } from '../classcalc/safety';
import type { GradedItem, Tier } from '../classcalc/safety';
import { chassisFor, licenceFor } from '../classcalc/credentials';
import type { Credential, Standing } from '../classcalc/credentials';
import {
  CHASSIS_LEVELS,
  DEFAULT_INTEREST,
  EMPTY_SPEC,
  ENGINE_FAMILIES,
  FRAMES,
  FRONT_SUSPENSIONS,
  FUELS,
  INTERESTS,
  POWER_ADDERS,
  REAR_SUSPENSIONS,
  TIRES,
  TIRE_WIDTHS,
  TRACK_LENGTHS,
  TRANSMISSIONS,
  VEHICLE_TYPES,
  parseNumber,
  resolvePerformance,
} from '../classcalc/spec';
import type { Interest, PerfSource, Performance, VehicleSpec } from '../classcalc/spec';

/**
 * Result group order — sanctioning bodies, then the drag-and-drive events, then
 * the radial scene, which is last because it is the one with no rulebook to
 * appeal to.
 */
const ORGS: Org[] = ['NHRA', 'IHRA', 'Drag Week', 'Sick Week', 'Radial'];

/** How the pace was arrived at, said plainly — this is §4.2's confidence indicator. */
const SOURCE_TEXT: Record<PerfSource, { label: string; note: string }> = {
  measured: {
    label: 'From your timeslip',
    note: 'Your own quarter-mile numbers, so the classes below are as good as this tool gets.',
  },
  converted: {
    label: 'Converted from the eighth',
    note: 'Converted at the ratio NHRA’s own 9.99 ≡ 6.39 and 11.49 ≡ 7.35 pairs share. Close to a threshold, read the rulebook’s eighth-mile column rather than this number.',
  },
  estimated: {
    label: 'Estimated from weight & power',
    note: 'Derived from race weight and flywheel horsepower. A combination that does not hook up runs slower than this — treat it as a ballpark, not a timeslip.',
  },
};

/**
 * Where the eighth-mile figure beside it came from.
 *
 * Kept apart from `SOURCE_TEXT` because the two can disagree: a racer who runs
 * the eighth is reading their own number in one column and a conversion in the
 * other, and which is which is the whole point of saying anything at all.
 */
const EIGHTH_TEXT: Record<PerfSource, string> = {
  measured: 'The eighth-mile ET is the number you gave — the quarter beside it is the converted one.',
  converted:
    'The eighth-mile ET is your quarter converted back at the same ratio, for eighth-mile tracks and the classes raced over them.',
  estimated: 'The eighth-mile ET is that same estimate over the shorter distance.',
};

/**
 * The three verdicts a credential card can carry.
 *
 * "Ask first" is the odd one and it is meant to be: it is the label on the one
 * combination the published rules do not settle (see `chassisFor`), and it has
 * to look different from both a requirement and a pass, or it reads as one of
 * them.
 */
const STANDING_TEXT: Record<Standing, string> = {
  required: 'Required',
  'not-required': 'Not required',
  ask: 'Ask first',
};

const TIER_TEXT: Record<Tier, string> = {
  required: 'Required',
  recommended: 'Recommended',
  'class-dependent': 'Class-dependent',
};

/**
 * The route, which is the catalogue and nothing else.
 *
 * Anything that is not one of the three canonical URLs — a typo, a slug in the
 * wrong case, the redundant `/both` — is sent to the one that answers it, so a
 * hand-typed link lands on a real page and a crawler never finds the same
 * catalogue twice at two addresses. The redirect replaces rather than pushes:
 * the URL was never somewhere the reader chose to be, and leaving it in history
 * would make Back a no-op that bounces them straight forward again.
 */
export function ClassCalc() {
  const { catalogue } = useParams();
  const interest = interestFromSlug(catalogue);
  const canonical = pathForInterest(interest ?? DEFAULT_INTEREST);

  if (interest === null || (catalogue !== undefined && canonical !== `${CLASSCALC_PATH}/${catalogue}`)) {
    return <Navigate to={canonical} replace />;
  }
  return <Calculator interest={interest} />;
}

function Calculator({ interest }: { interest: Interest }) {
  const navigate = useNavigate();
  const page = CATALOGUE_PAGE[interest];
  usePageMeta(page.title, pathForInterest(interest), page.description);

  const [draft, setDraft] = useState<VehicleSpec>(EMPTY_SPEC);
  // The numeric fields are held as text: parsing on every keystroke would fight
  // anyone typing "10." on the way to "10.50".
  const [text, setText] = useState({ weight: '', hp: '', ci: '', et: '', mph: '', bodyYear: '' });

  // The catalogue comes off the URL rather than out of `draft`, so there is one
  // copy of it and the Back button works on it like it works on anything else.
  // Everything the racer typed lives in `draft` and survives the switch: this
  // component keeps its place in the tree across a catalogue change, so state is
  // not remounted away.
  const spec = useMemo<VehicleSpec>(() => ({ ...draft, interest }), [draft, interest]);
  // Two different audiences for two blocks of the form: the drag-and-drive
  // detail is only Drag Week and Sick Week, but frame and suspension are asked
  // for by those events *and* by the radial classes.
  const wantsDnd = interest === 'Drag Week / Sick Week' || interest === 'All';
  const wantsBuild = interest !== 'NHRA / IHRA';

  const set = <K extends keyof VehicleSpec>(k: K, v: VehicleSpec[K]) => setDraft((s) => ({ ...s, [k]: v }));
  const setNum = (k: keyof typeof text, field: keyof VehicleSpec, v: string) => {
    setText((t) => ({ ...t, [k]: v }));
    setDraft((s) => ({ ...s, [field]: parseNumber(v) }));
  };

  const perf = useMemo(() => resolvePerformance(spec), [spec]);
  const matches = useMemo(() => (perf ? matchClasses(spec, perf.et) : []), [spec, perf]);
  const groups = useMemo(
    () =>
      perf
        ? checklistByCategory({
            et: perf.et,
            mph: perf.mph,
            vehicleType: spec.vehicleType,
            transmission: spec.transmission,
            tire: spec.tire,
            powerAdder: spec.powerAdder,
            fuel: spec.fuel,
            chassis: spec.chassis,
            batteryRelocated: spec.batteryRelocated,
            dragAndDrive: wantsDnd,
          })
        : [],
    [spec, perf, wantsDnd],
  );
  const counts = useMemo(() => tierCounts(groups.flatMap((g) => g.items)), [groups]);
  // The two checklist rows that need a paragraph rather than a line. Same
  // inputs as the checklist, deliberately: an explainer that could disagree
  // with the requirement it explains would be worse than no explainer.
  const creds = useMemo(() => {
    if (!perf) return null;
    const c = {
      et: perf.et,
      mph: perf.mph,
      vehicleType: spec.vehicleType,
      chassis: spec.chassis,
      fuel: spec.fuel,
    };
    return { licence: licenceFor(c), chassis: chassisFor(c) };
  }, [perf, spec.vehicleType, spec.chassis, spec.fuel]);

  return (
    <div className="svcdue">
      <main className="main cc">
        <Link to="/" className="link legal-back">
          <ArrowLeft size={13} /> Back to SvcDue
        </Link>

        <Eyebrow>Free tool · no sign-in</Eyebrow>
        <h1 className="h1">Class calculator</h1>
        {/* The middle sentence follows the catalogue: someone who arrived on the
            Drag Week link should be told they are on the Drag Week page, not
            handed a paragraph about both and left to check a toggle. */}
        <p className="lede">
          What class does this combination fit, what does the rulebook want bolted to it, and — if it needs a licence or
          a certified chassis — what does getting one actually involve? {page.lede} Enter what you know: the more of it,
          the better the answer. Nothing you type is sent anywhere or saved.
        </p>

        {/* ---- the spec ---- */}
        <section aria-label="Vehicle spec">
          <div className="form-sec">
            <Gauge size={12} /> Pace
            <span className="sec-hint">a timeslip if you have one, weight and power if you don't</span>
          </div>

          <div className="field-grid-2">
            <Field label="Race weight" hint="lb, with the driver in it">
              <div className="input-suffix">
                <input
                  className="input"
                  value={text.weight}
                  onChange={(e) => setNum('weight', 'weight', e.target.value)}
                  inputMode="numeric"
                  placeholder="3,150"
                />
                <span>lb</span>
              </div>
            </Field>
            <Field label="Horsepower" hint="at the flywheel — an estimate is fine">
              <div className="input-suffix">
                <input
                  className="input"
                  value={text.hp}
                  onChange={(e) => setNum('hp', 'hp', e.target.value)}
                  inputMode="numeric"
                  placeholder="620"
                />
                <span>hp</span>
              </div>
            </Field>
          </div>

          <Field label="Track length" hint="which numbers below are you giving?">
            <Opts
              options={TRACK_LENGTHS}
              value={spec.trackLength}
              onChange={(v) => set('trackLength', v as VehicleSpec['trackLength'])}
            />
          </Field>

          <div className="field-grid-3">
            <Field label="Best ET" hint="seconds — if you know it">
              <input
                className="input"
                value={text.et}
                onChange={(e) => setNum('et', 'bestEt', e.target.value)}
                inputMode="decimal"
                placeholder="10.42"
              />
            </Field>
            <Field label="Trap speed" hint="mph — if you know it">
              <input
                className="input"
                value={text.mph}
                onChange={(e) => setNum('mph', 'bestMph', e.target.value)}
                inputMode="decimal"
                placeholder="128"
              />
            </Field>
            <Field label="Displacement" hint="cubic inches — helps for Comp & Stock">
              <div className="input-suffix">
                <input
                  className="input"
                  value={text.ci}
                  onChange={(e) => setNum('ci', 'cubicInches', e.target.value)}
                  inputMode="numeric"
                  placeholder="376"
                />
                <span>ci</span>
              </div>
            </Field>
          </div>

          <div className="form-sec">
            <Route size={12} /> Where are you racing?
            <span className="sec-hint">each catalogue answers a different question</span>
          </div>
          {/* The one control that moves the URL, so a racer who has narrowed it
              down can send the link rather than an instruction to press a
              button. It replaces rather than pushes: three buttons someone tries
              in turn would otherwise bury the page they came from under three
              history entries, and Back should still mean back. */}
          <Field label="Catalogue">
            <Opts
              options={INTERESTS}
              value={interest}
              onChange={(v) => navigate(pathForInterest(v as Interest), { replace: true })}
            />
          </Field>

          <div className="form-sec">
            <Wrench size={12} /> The combination
          </div>

          <Field label="Body style">
            <Opts
              options={VEHICLE_TYPES}
              value={spec.vehicleType}
              onChange={(v) => set('vehicleType', v as VehicleSpec['vehicleType'])}
            />
          </Field>
          <Field label="Power adder">
            <Opts
              options={POWER_ADDERS}
              value={spec.powerAdder}
              onChange={(v) => set('powerAdder', v as VehicleSpec['powerAdder'])}
            />
          </Field>
          <Field label="Tires" hint="radial or slick is its own rule, separate from how wide">
            <Opts options={TIRES} value={spec.tire} onChange={(v) => set('tire', v as VehicleSpec['tire'])} />
          </Field>
          {/* Asked of everyone, unlike the build questions below, because it is
              the one answer here that changes the safety list rather than the
              class list — a blown methanol car owes two more layers of suit. */}
          <Field label="Fuel" hint="alcohol changes what the driver has to be wearing">
            <Opts options={FUELS} value={spec.fuel} onChange={(v) => set('fuel', v as VehicleSpec['fuel'])} />
          </Field>

          {/* Only asked for when it is going to be used. None of this decides
              anything in NHRA or IHRA, and a pile of extra questions on the way
              to an answer that ignores them is how a form stops getting filled
              in. The build block is shared: Drag Week and the radial classes
              both turn on it, and they are the two catalogues that write
              separate FRAME, FRONT SUSPENSION and REAR SUSPENSION rules. */}
          {wantsBuild && (
            <>
              <div className="form-sec">
                <Wrench size={12} /> How it's built
                <span className="sec-hint">three separate rules in the rulebooks, so three questions here</span>
              </div>

              <Field label="Frame">
                <Opts options={FRAMES} value={spec.frame} onChange={(v) => set('frame', v as VehicleSpec['frame'])} />
              </Field>
              <Field
                label="Front suspension"
                hint="aftermarket arms, coil-overs and K-members still count as stock-type if they use the factory pickup points"
              >
                <Opts
                  options={FRONT_SUSPENSIONS}
                  value={spec.frontSuspension}
                  onChange={(v) => set('frontSuspension', v as VehicleSpec['frontSuspension'])}
                />
              </Field>
              <Field
                label="Rear suspension"
                hint="same rule: bolt-in parts in the original locations are stock-type, a coil-over conversion on a coil-spring car included"
              >
                <Opts
                  options={REAR_SUSPENSIONS}
                  value={spec.rearSuspension}
                  onChange={(v) => set('rearSuspension', v as VehicleSpec['rearSuspension'])}
                />
              </Field>
            </>
          )}

          {wantsDnd && (
            <>
              <div className="form-sec">
                <Route size={12} /> Drag-and-drive detail
                <span className="sec-hint">what Drag Week and Sick Week actually split on</span>
              </div>

              <Field label="Tread width" hint="measured at the tread, 20 psi, car's weight on it">
                <Opts
                  options={TIRE_WIDTHS}
                  value={spec.tireWidth}
                  onChange={(v) => set('tireWidth', v as VehicleSpec['tireWidth'])}
                />
              </Field>
              <Field label="Engine family" hint="a big-block architecture counts as big-block at any size">
                <Opts
                  options={ENGINE_FAMILIES}
                  value={spec.engineFamily}
                  onChange={(v) => set('engineFamily', v as VehicleSpec['engineFamily'])}
                />
              </Field>
              <Field label="Body year" hint="for the Gasser and Hot Rod year windows">
                <input
                  className="input"
                  value={text.bodyYear}
                  onChange={(e) => setNum('bodyYear', 'bodyYear', e.target.value)}
                  inputMode="numeric"
                  placeholder="1969"
                />
              </Field>

              <div className="safety-list">
                <TrigToggle
                  icon={Route}
                  name="Registered, insured, plated and road-legal"
                  sub="the gate on every drag-and-drive class — you drive the route yourself"
                  on={spec.streetLegal}
                  onToggle={() => set('streetLegal', !spec.streetLegal)}
                  noInput
                />
              </div>
            </>
          )}
          <div className="field-grid-2">
            <Field label="Chassis">
              <select
                className="select"
                value={spec.chassis}
                onChange={(e) => set('chassis', e.target.value as VehicleSpec['chassis'])}
              >
                {CHASSIS_LEVELS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Field>
            <Field label="Transmission">
              <select
                className="select"
                value={spec.transmission}
                onChange={(e) => set('transmission', e.target.value as VehicleSpec['transmission'])}
              >
                {TRANSMISSIONS.map((o) => (
                  <option key={o}>{o}</option>
                ))}
              </select>
            </Field>
          </div>

          <div className="form-sec">Fitted</div>
          <div className="safety-list">
            <TrigToggle
              icon={Sparkles}
              name="Delay box or throttle stop"
              sub="the line between the footbrake and electronics brackets"
              on={spec.electronics}
              onToggle={() => set('electronics', !spec.electronics)}
              noInput
            />
            <TrigToggle
              icon={Flag}
              name="Parachute"
              sub="mandatory at 150 mph"
              on={spec.parachute}
              onToggle={() => set('parachute', !spec.parachute)}
              noInput
            />
            <TrigToggle
              icon={Wrench}
              name="Wheelie bars"
              sub="never required, and banned in some classes"
              on={spec.wheelieBars}
              onToggle={() => set('wheelieBars', !spec.wheelieBars)}
              noInput
            />
            <TrigToggle
              icon={ShieldCheck}
              name="Fuel cell"
              sub="class-dependent, and mandatory at 7.49"
              on={spec.fuelCell}
              onToggle={() => set('fuelCell', !spec.fuelCell)}
              noInput
            />
            <TrigToggle
              icon={ShieldCheck}
              name="Battery moved from its factory location"
              sub="pulls in a battery box and a master cutoff"
              on={spec.batteryRelocated}
              onToggle={() => set('batteryRelocated', !spec.batteryRelocated)}
              noInput
            />
          </div>
        </section>

        {/* ---- the answer ---- */}
        {!perf ? (
          <div className="cc-empty">
            <CircleHelp size={16} />
            <div>
              <b>Give it something to work with.</b>
              <span>
                Either a best ET, or a race weight and a horsepower figure. Everything below follows from the pace.
              </span>
            </div>
          </div>
        ) : (
          <>
            <Pace perf={perf} />
            <Classes matches={matches} />
            <Checklist groups={groups} counts={counts} />
            {creds && <Credentials licence={creds.licence} chassis={creds.chassis} />}
          </>
        )}

        <Sources />
      </main>
      <Footer />
    </div>
  );
}

function Pace({ perf }: { perf: Performance }) {
  const { et, mph, source, mixed, eighthEt, eighthSource, mphLength } = perf;
  const { label, note } = SOURCE_TEXT[source];
  return (
    <section className="cc-pace" aria-label="Working pace">
      <div className="cc-pace-nums">
        <div>
          <span>Quarter-mile ET</span>
          <b>{et.toFixed(2)}</b>
        </div>
        {/* Half the sport never sees a quarter. Printing both costs one tile and
            saves an eighth-mile racer doing the division themselves — badly, in
            the staging lanes, against a threshold that matters. */}
        <div>
          <span>Eighth-mile ET</span>
          <b>{eighthEt.toFixed(2)}</b>
        </div>
        <div>
          {/* Labelled with its length, because there are now two ETs above it
              and a bare "trap speed" would be the reader's guess as to which. */}
          <span>Trap speed{mphLength === null ? '' : ` · ${mphLength}`}</span>
          <b>{mph === null ? '—' : Math.round(mph)}</b>
        </div>
      </div>
      <div className="cc-pace-src">
        <span className={'cc-conf cc-conf-' + source}>{label}</span>
        <p>
          {note} {EIGHTH_TEXT[eighthSource]}
          {mixed && ' One of the two numbers is yours and the other is estimated.'}
          {/* The one thing that genuinely does not carry across the two columns,
              said rather than left to be inferred from the label. */}
          {mph !== null && ' Trap speed does not convert between the two lengths, so it is shown only at its own.'}
          {/* Said out loud rather than left as an em-dash. Several thresholds are
              "this ET *or* that speed", and without a speed only half of each
              can fire — a racer needs to know which half they are missing. */}
          {mph === null &&
            ' No trap speed given, so the rules that trigger on speed alone — the 135 and 150 mph lines — are not being applied. Add it if you know it.'}
        </p>
      </div>
    </section>
  );
}

/**
 * Grouped by sanctioning body / event, in catalogue order, dropping the ones
 * with nothing in them. Four possible groups now, so this can no longer be the
 * hardcoded NHRA-then-IHRA pair it started as.
 */
function byOrg(list: ClassMatch[]) {
  return ORGS.map((org) => ({ org, list: list.filter((m) => m.cls.org === org) })).filter((g) => g.list.length > 0);
}

function Classes({ matches }: { matches: ClassMatch[] }) {
  const { shown, unjudged, held } = shortlist(matches);
  // Collapsed by default, and deliberately not remembered: the shortlist is the
  // answer, and the held list is the follow-up question.
  const [showHeld, setShowHeld] = useState(false);

  const groups = byOrg(shown);
  const dnd = shown.some((m) => m.cls.shape === 'drag-and-drive');

  return (
    <section aria-label="Class suggestions">
      <div className="row-h">
        <h2 className="h2">Classes that fit</h2>
        <span className="row-sub">
          {shown.length} candidate{shown.length === 1 ? '' : 's'}
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="rule-empty">
          Nothing in the sportsman catalogue lines up with that pace. Very quick or very slow combinations are usually a
          conversation with a tech official rather than a lookup.
        </div>
      ) : (
        <div className="cc-orgs">
          {groups.map(({ org, list }) => (
            <div key={org}>
              <div className="cc-org-h">{org}</div>
              {list.map((m) => (
                <ClassCard key={m.cls.key} match={m} />
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Named, but never ranked. These turn on something the form cannot ask
          — a subjective appearance call, or "is it a Beetle" — and a green
          "strong match" on a class the engine has not actually checked would be
          a lie told confidently. */}
      {unjudged.length > 0 && (
        <div className="cc-unjudged">
          <b>Also at these events, and not something this page can judge:</b>
          <ul>
            {unjudged.map((m) => (
              <li key={m.cls.key}>
                <span className="cc-unjudged-name">
                  {m.cls.org} — {m.cls.name}
                </span>
                <span className="cc-unjudged-why">{m.cls.unjudged}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Both drag-and-drive rulebooks say it outright, and it changes how the
          list above should be read: matching six classes does not mean picking
          the friendliest of the six. */}
      {dnd && (
        <p className="cc-authority">
          <CircleHelp size={12} /> Drag Week and Sick Week both require you to enter the{' '}
          <b className="c-ink">lowest class you are eligible for</b> — a match here is not a menu.
        </p>
      )}

      {/* No silent truncation: a shortlist that hid the rest would read as the
          whole answer. And the count on its own was only half the honesty —
          "thirty classes are out" does not tell you that a tube chassis and a
          four-link are what put them there, which is the one thing a racer
          deciding what to build next actually wants. So the count opens. */}
      {held.length > 0 && (
        <div className="cc-held">
          <p className="cc-authority">
            <TriangleAlert size={12} />
            <span>
              {held.length} more {held.length === 1 ? 'class is' : 'classes are'} two or more changes away
              {showHeld ? '.' : ', and not listed.'}
            </span>
            <button
              type="button"
              className="cc-held-btn"
              onClick={() => setShowHeld((v) => !v)}
              aria-expanded={showHeld}
            >
              {showHeld ? 'Hide them' : 'Show them anyway'}
            </button>
          </p>

          {showHeld && (
            <div className="cc-held-list">
              {/* Said before the cards, because a list of red cards with no
                  framing reads as "you failed thirty classes" rather than as
                  what it is: the same combination measured against classes it
                  was never close to. */}
              <p className="cc-held-note">
                Every one of these is out on at least two counts, listed on the card. Most of them are out for the same
                one or two reasons — the frame, the suspension, the tire — so this is a way to see what a build decision
                already cost you, not a to-do list.
              </p>
              <div className="cc-orgs">
                {byOrg(held).map(({ org, list }) => (
                  <div key={org}>
                    <div className="cc-org-h">{org}</div>
                    {list.map((m) => (
                      <ClassCard key={m.cls.key} match={m} out />
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* §4.2: say who actually decides, every single time. */}
      <p className="cc-authority">
        <TriangleAlert size={12} /> Entry is decided by the sanctioning body and the tech inspector, not by this page.
      </p>
    </section>
  );
}

/**
 * `out` is a held class — one shown only because the racer asked to see what
 * ruled it out. It carries the same content, dimmed and labelled as out of
 * reach, so it can never be mistaken for a candidate while scrolling past.
 */
function ClassCard({ match, out = false }: { match: ClassMatch; out?: boolean }) {
  const { cls, quality, fits, blockers } = match;
  return (
    <article className={'cc-class cc-class-' + quality + (out ? ' cc-class-out' : '')}>
      <header className="cc-class-h">
        <div>
          <b>{cls.name}</b>
          <span className="cc-class-shape">
            {cls.index !== undefined ? `${cls.index.toFixed(2)} index` : cls.shape}
            {/* An overlay is not a class you enter instead of your own — it is
                one you are also in the running for. Saying so on the card stops
                it reading as a competing option. */}
            {cls.overlay && ' · award, on top of your class'}
          </span>
        </div>
        <span className={'cc-q cc-q-' + quality}>
          {out ? `${blockers.length} changes away` : QUALITY_TEXT[quality]}
        </span>
      </header>
      <p className="cc-class-blurb">{cls.blurb}</p>

      {fits.length > 0 && (
        <ul className="cc-fits">
          {fits.map((f, i) => (
            <li key={i}>
              <CircleCheck size={12} /> {f}
            </li>
          ))}
        </ul>
      )}
      {blockers.length > 0 && (
        <ul className="cc-blockers">
          {blockers.map((b, i) => (
            <li key={i}>
              <TriangleAlert size={12} /> {b}
            </li>
          ))}
        </ul>
      )}
      {cls.notes.length > 0 && (
        <ul className="cc-notes">
          {cls.notes.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      )}
    </article>
  );
}

function Checklist({
  groups,
  counts,
}: {
  groups: { category: string; items: GradedItem[] }[];
  counts: Record<Tier, number>;
}) {
  return (
    <section aria-label="Safety requirements">
      <div className="row-h">
        <h2 className="h2">Minimum safety equipment</h2>
        <span className="row-sub">at this pace</span>
      </div>

      <div className="cc-tally">
        <span className="cc-tally-i">
          <i className="cc-tier-dot cc-tier-required" />
          {counts.required} required
        </span>
        <span className="cc-tally-i">
          <i className="cc-tier-dot cc-tier-recommended" />
          {counts.recommended} recommended
        </span>
        <span className="cc-tally-i">
          <i className="cc-tier-dot cc-tier-class-dependent" />
          {counts['class-dependent']} class-dependent
        </span>
      </div>

      {groups.map((group) => (
        <div className="cc-group" key={group.category}>
          <div className="cc-group-h">
            <ListChecks size={13} /> {group.category}
          </div>
          {group.items.map((item) => (
            <div className={'cc-item cc-item-' + item.tier} key={item.key}>
              <span className={'cc-tier cc-tier-' + item.tier}>{TIER_TEXT[item.tier]}</span>
              <div className="cc-item-main">
                <b>{item.label}</b>
                {item.spec && <span className="cc-item-spec">{item.spec}</span>}
                <span className="cc-item-why">{item.why}</span>
              </div>
            </div>
          ))}
        </div>
      ))}

      <div className="safety-note">
        <TriangleAlert size={15} />
        <div>
          <b>A planning aid, not the rulebook.</b>
          <span>
            {DISCLAIMER} Requirements move with your class, body style and sanctioning body, and both rulebooks are
            revised every season — this list is the general-regulations ladder, which is the floor rather than the whole
            answer.
          </span>
        </div>
      </div>
    </section>
  );
}

/**
 * "…and how do I actually get one?"
 *
 * The checklist above can say that a 9.60 car owes a competition licence and a
 * certified chassis. It cannot say what either of those *is*, and those two
 * rows are where this page was losing people: two pieces of feedback arrived
 * about them — one about the licence levels, one about the chassis SFI levels —
 * and both asked for the same thing, explain it so it isn't frightening.
 *
 * So this section sits directly under the list that raised them, answers the
 * next question in the order a racer meets it, and puts the reassurance in the
 * copy rather than in the styling. The cards are quiet: a licence you do not
 * need should not arrive in a red box, and one you do need should not either.
 */
function Credentials({ licence, chassis }: { licence: Credential; chassis: Credential }) {
  return (
    <section aria-label="Licence and chassis certification">
      <div className="row-h">
        <h2 className="h2">Licence &amp; chassis certification</h2>
        <span className="row-sub">what they are, and how you get one</span>
      </div>

      <p className="cc-cred-intro">
        The two entries on that list that sound worst — and at plenty of paces neither one is required at all. Here is
        where this combination stands on each, what the rulebook actually asks for, and what the day itself looks like.
      </p>

      <div className="cc-creds">
        <CredentialCard c={licence} icon={IdCard} />
        <CredentialCard c={chassis} icon={Stamp} />
      </div>

      {/* Same rule as everywhere else on this page: name who decides. Fees and
          levels in particular are NHRA's to change between one season and the
          next, and the form in their hand beats the copy in ours. */}
      <p className="cc-authority">
        <TriangleAlert size={12} /> Levels, fees and forms are NHRA’s and change without notice — the application and
        your division office are the authority, not this page.
      </p>
    </section>
  );
}

function CredentialCard({ c, icon: Icon }: { c: Credential; icon: LucideIcon }) {
  return (
    <article className={'cc-cred cc-cred-' + c.standing}>
      <header className="cc-cred-h">
        <div>
          <span className="cc-cred-kind">
            <Icon size={11} /> {c.title}
          </span>
          <b>{c.headline}</b>
        </div>
        <span className={'cc-q cc-q-' + (c.standing === 'required' ? 'likely' : 'possible')}>
          {STANDING_TEXT[c.standing]}
        </span>
      </header>

      {/* The rule first, then the plain-English version of it. In that order:
          somebody who only trusts the rulebook gets what they came for without
          reading a word of the reassurance underneath. */}
      <p className="cc-cred-why">{c.why}</p>
      <p className="cc-cred-lede">{c.lede}</p>

      <ol className="cc-steps">
        {c.steps.map((step, i) => (
          <li key={step.what}>
            <span className="cc-step-n">{i + 1}</span>
            <div>
              <b>{step.what}</b>
              <span>{step.detail}</span>
            </div>
          </li>
        ))}
      </ol>

      <dl className="cc-facts">
        {c.facts.map((fact) => (
          <div key={fact.label}>
            <dt>{fact.label}</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>

      {/* The sentence that answers the fear rather than the question. It is the
          whole point of the card, so it is last and it is marked. */}
      <p className="cc-cred-calm">
        <CircleCheck size={12} /> <span>{c.calm}</span>
      </p>

      <div className="cc-cred-links">
        {c.links.map((link) => (
          <a key={link.url} href={link.url} target="_blank" rel="noreferrer noopener">
            {link.label} <ExternalLink size={11} />
          </a>
        ))}
      </div>
    </article>
  );
}

function Sources() {
  return (
    <section className="cc-sources" aria-label="Rule sources">
      <div className="row-h">
        <h2 className="h2">Where these rules come from</h2>
        <span className="row-sub">
          {RULES_SEASON} season · checked {RULES_CHECKED}
        </span>
      </div>
      <div className="cc-source-list">
        {RULE_SOURCES.map((s) => (
          <a className="cc-source" key={s.url} href={s.url} target="_blank" rel="noreferrer noopener">
            <b>
              {s.label} <ExternalLink size={12} />
            </b>
            <span>{s.note}</span>
          </a>
        ))}
      </div>
      <p className="landing-caveat">
        SvcDue itself tracks what you own and tells you when it expires — belts, chutes, helmets, cage recerts. This page
        is free and needs no account. <Link to="/">See what SvcDue does →</Link>
      </p>
    </section>
  );
}
